import React, { useRef, useEffect } from "react";
import { Group, Rect, Text, Transformer, Line, Circle } from "react-konva";
import { GfFlyerItemDto, Coordinate } from "@swagger/typescript-fetch-goflyer";
import Konva from "konva";
import { COORDINATE_BOX_CONSTANTS, VIEWER_COLORS } from "../constants";
import { useFlyerCoordinateEditorContext } from "../context/FlyerCoordinateEditorContext";

/**
 * Props for the CoordinateBox component.
 *
 * @property flyerItem - The flyer item data containing name, price, quantity, and other details
 * @property coordinate - Coordinate object defining the box position and dimensions
 * @property isSelected - Whether this box is currently selected (shows transformer and info card)
 * @property onSelect - Callback invoked when the box is double-clicked to select it
 * @property onDragStart - Callback invoked when dragging begins (used to disable stage dragging)
 * @property onDragEnd - Callback invoked when dragging ends, receives updated coordinate
 * @property onTransformEnd - Callback invoked when resizing/transforming ends, receives updated coordinate
 * @property onEdit - Optional callback invoked when edit icon is clicked
 */
interface CoordinateBoxProps {
  flyerItem: GfFlyerItemDto;
  coordinate: Coordinate;
  isSelected: boolean;
  onSelect: () => void;
  onDragStart: () => void;
  onDragMove?: (newCoordinate: Coordinate) => void;
  onDragEnd: (newCoordinate: Coordinate) => void;
  onTransformEnd: (newCoordinate: Coordinate) => void;
  onEdit?: () => void;
  isAIExtraction?: boolean;
  hideInfoOverlay?: boolean;
}

/**
 * Renders a coordinate box on the flyer image with interactive controls.
 *
 * This component visualizes a coordinate boundary box on the flyer canvas and displays
 * an attached information card when selected. The box can be dragged, resized via
 * transformer handles, and double-clicked to select/deselect.
 *
 * Visual components:
 * - Rectangle: The coordinate boundary box with colored stroke (teal when selected, orange otherwise)
 * - Transformer: Resize handles shown when selected (8 handles for edges and corners)
 * - Information Card: Floating card attached via connector line (only visible when selected)
 * - Connector Line: Connects the card to the box with a dot indicator
 * - Edit Icon: Optional pencil icon in top-right of card for editing the item
 *
 * Information card displays:
 * - Item name (with word wrap for long names)
 * - Price (if available)
 * - Quantity and unit (if available)
 * - Details shown in a light blue pill background
 *
 * Coordinate system:
 * - All coordinates are stored in original image space (not scaled)
 * - Component applies scaleX/scaleY transformations from FlyerCoordinateEditorContext
 * - Positions are offset by imageOffset to account for centering
 * - Transformations are normalized back to scale=1 to maintain data consistency
 *
 * Interaction features:
 * - Double-click box to select
 * - Drag box when selected (clamped to image bounds)
 * - Resize via transformer handles (enforces minimum size, clamped to bounds)
 * - Click edit icon to trigger onEdit callback
 * - Hover effects on edit icon
 *
 * @param props - Component properties defining the box, item data, and callbacks
 */
export const CoordinateBox: React.FC<CoordinateBoxProps> = ({
  flyerItem,
  coordinate,
  isSelected,
  onSelect,
  onDragStart,
  onDragMove,
  onDragEnd,
  onTransformEnd,
  onEdit,
  isAIExtraction = false,
  hideInfoOverlay = false,
}) => {
  const { scaleX, scaleY, imageBounds, imageOffset } =
    useFlyerCoordinateEditorContext();
  const rectRef = useRef<Konva.Rect>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const groupRef = useRef<Konva.Group>(null);

  useEffect(() => {
    if (isSelected && rectRef.current && transformerRef.current) {
      transformerRef.current.nodes([rectRef.current]);
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected]);

  /**
   * Handles box drag end, converting layer coordinates to image coordinates and clamping to bounds.
   *
   * When the user finishes dragging the box:
   * 1. Gets the box's position in layer coordinates (includes stage offset)
   * 2. Converts to image coordinates by subtracting imageOffset and dividing by scale
   * 3. Clamps position to ensure box stays within image bounds
   * 4. Applies clamped position back to the node for visual feedback
   * 5. Invokes onDragEnd callback with updated coordinate in image space
   *
   * Bounds checking:
   * - X clamped between 0 and (imageWidth - boxWidth)
   * - Y clamped between 0 and (imageHeight - boxHeight)
   *
   * This ensures the box cannot be dragged outside the image boundaries.
   *
   * @param e - Konva drag event containing target node with position
   */
  const handleDragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
    const node = e.target;
    let newX = (node.x() - imageOffset.x) / scaleX;
    let newY = (node.y() - imageOffset.y) / scaleY;

    const maxX = imageBounds.width - coordinate.component.width;
    const maxY = imageBounds.height - coordinate.component.height;

    newX = Math.max(0, Math.min(newX, maxX));
    newY = Math.max(0, Math.min(newY, maxY));

    node.x(imageOffset.x + newX * scaleX);
    node.y(imageOffset.y + newY * scaleY);

    onDragEnd({
      ...coordinate,
      x: newX,
      y: newY,
    });
  };

  const handleDragMove = (e: Konva.KonvaEventObject<DragEvent>) => {
    const node = e.target;
    let newX = (node.x() - imageOffset.x) / scaleX;
    let newY = (node.y() - imageOffset.y) / scaleY;

    const maxX = imageBounds.width - coordinate.component.width;
    const maxY = imageBounds.height - coordinate.component.height;

    newX = Math.max(0, Math.min(newX, maxX));
    newY = Math.max(0, Math.min(newY, maxY));

    node.x(imageOffset.x + newX * scaleX);
    node.y(imageOffset.y + newY * scaleY);

    onDragMove?.({
      ...coordinate,
      x: newX,
      y: newY,
    });
  };

  /**
   * Handles box transformation end, normalizing scale and clamping dimensions to image bounds.
   *
   * When the user finishes resizing the box via transformer handles:
   * 1. Gets the node's current scale (applied by transformer)
   * 2. Normalizes scale back to 1 (to maintain data consistency)
   * 3. Calculates new dimensions by applying scale to width/height
   * 4. Converts position to image coordinates (dividing by scaleX/Y)
   * 5. Enforces minimum size constraint from COORDINATE_BOX_CONSTANTS
   * 6. Clamps dimensions to prevent exceeding image bounds
   * 7. Applies normalized values back to node for rendering
   * 8. Invokes onTransformEnd callback with updated coordinate in image space
   *
   * Bounds checking:
   * - Position clamped to start at 0 or greater
   * - Width/height clamped to MIN_SIZE or greater
   * - Right edge: if x + width > imageWidth, width is reduced
   * - Bottom edge: if y + height > imageHeight, height is reduced
   *
   * Scale normalization is critical because Konva applies scale transforms during
   * resizing, but we store actual width/height values in our coordinate data.
   */
  const handleTransformEnd = () => {
    const node = rectRef.current;
    if (!node) return;

    const scaleX_node = node.scaleX();
    const scaleY_node = node.scaleY();

    node.scaleX(1);
    node.scaleY(1);

    let newWidth = (node.width() * scaleX_node) / scaleX;
    let newHeight = (node.height() * scaleY_node) / scaleY;
    let newX = (node.x() - imageOffset.x) / scaleX;
    let newY = (node.y() - imageOffset.y) / scaleY;

    newX = Math.max(0, newX);
    newY = Math.max(0, newY);
    newWidth = Math.max(COORDINATE_BOX_CONSTANTS.MIN_SIZE, newWidth);
    newHeight = Math.max(COORDINATE_BOX_CONSTANTS.MIN_SIZE, newHeight);

    if (newX + newWidth > imageBounds.width) {
      newWidth = imageBounds.width - newX;
    }
    if (newY + newHeight > imageBounds.height) {
      newHeight = imageBounds.height - newY;
    }

    node.width(newWidth * scaleX);
    node.height(newHeight * scaleY);
    node.x(imageOffset.x + newX * scaleX);
    node.y(imageOffset.y + newY * scaleY);

    onTransformEnd({
      ...coordinate,
      x: newX,
      y: newY,
      component: {
        ...coordinate.component,
        width: newWidth,
        height: newHeight,
      },
    });
  };

  const displayName =
    flyerItem.name ||
    flyerItem.nameChinese ||
    COORDINATE_BOX_CONSTANTS.DISPLAY_NAME_FALLBACK;

  const priceText = flyerItem.salePrice
    ? `$${flyerItem.salePrice.toFixed(2)}`
    : "";

  const unitText = flyerItem?.unit || flyerItem.unitV2?.name || "";
  const quantityText = flyerItem?.quantity ? `${flyerItem.quantity}` : "";

  const unitQuantityText = [quantityText, unitText].filter(Boolean).join(" ");

  const detailsText = [priceText, unitQuantityText].filter(Boolean).join(" / ");

  const cardWidth = COORDINATE_BOX_CONSTANTS.CARD.WIDTH;

  const avgCharWidth = COORDINATE_BOX_CONSTANTS.TEXT.AVG_CHAR_WIDTH;
  const titleContentWidth =
    cardWidth -
    COORDINATE_BOX_CONSTANTS.CARD.CONTENT_WIDTH_SUBTRACT -
    COORDINATE_BOX_CONSTANTS.CARD.EDIT_ICON_SIZE;
  const titleLines = Math.ceil(
    (displayName.length * avgCharWidth) / titleContentWidth,
  );

  const detailsContentWidth =
    cardWidth - COORDINATE_BOX_CONSTANTS.CARD.DETAILS_CONTENT_WIDTH_SUBTRACT;
  const detailsLines = detailsText
    ? Math.ceil((detailsText.length * 4) / detailsContentWidth)
    : 0;

  const titleHeight = Math.max(
    titleLines * COORDINATE_BOX_CONSTANTS.CARD.TITLE_LINE_HEIGHT_PX,
    COORDINATE_BOX_CONSTANTS.CARD.TITLE_MIN_HEIGHT,
  );
  const detailsHeight =
    detailsLines * COORDINATE_BOX_CONSTANTS.CARD.DETAILS_LINE_HEIGHT_PX;
  const cardPadding = COORDINATE_BOX_CONSTANTS.CARD.PADDING;
  const cardHeight =
    cardPadding +
    titleHeight +
    (detailsText
      ? COORDINATE_BOX_CONSTANTS.CARD.DETAILS_SECTION_GAP +
        detailsHeight +
        COORDINATE_BOX_CONSTANTS.CARD.DETAILS_SECTION_GAP
      : COORDINATE_BOX_CONSTANTS.CARD.DETAILS_SECTION_GAP);

  const boxX = imageOffset.x + coordinate.x * scaleX;
  const boxY = imageOffset.y + coordinate.y * scaleY;
  const cardX = boxX - cardWidth - COORDINATE_BOX_CONSTANTS.CARD.OFFSET_X;
  const cardY = boxY;

  const connectorStartX = cardX + cardWidth;
  const connectorStartY = cardY + cardHeight / 2;
  const connectorEndX = boxX;
  const connectorEndY = boxY + COORDINATE_BOX_CONSTANTS.CONNECTOR.END_Y_OFFSET;

  const editIconSize = COORDINATE_BOX_CONSTANTS.CARD.EDIT_ICON_SIZE;
  const editIconPadding = COORDINATE_BOX_CONSTANTS.CARD.EDIT_ICON_PADDING;
  const boxEditIconX =
    boxX + coordinate.component.width * scaleX - editIconSize - editIconPadding;
  const boxEditIconY = boxY + editIconPadding;

  const [isHovered, setIsHovered] = React.useState(false);
  const [isEditHovered, setIsEditHovered] = React.useState(false);

  useEffect(() => {
    if (isSelected && groupRef.current) {
      groupRef.current.moveToTop();
    }
  }, [isSelected]);

  /**
   * Handles edit icon click, preventing event bubbling to box selection.
   *
   * When the user clicks the edit icon:
   * 1. Prevents event from bubbling up to parent groups (cancelBubble = true)
   * 2. Invokes the onEdit callback if provided
   *
   * Event bubbling prevention is essential to avoid triggering box selection
   * or other click handlers when the user intends to edit the item.
   *
   * @param e - Konva mouse event to cancel bubbling
   */
  const handleEditClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    e.cancelBubble = true;
    if (onEdit) {
      onEdit();
    }
  };

  return (
    <Group
      ref={groupRef}
      onDragStart={(e) => {
        e.cancelBubble = true;
      }}
      onDragMove={(e) => {
        e.cancelBubble = true;
      }}
      data-testid={`flyer-coordinate-box-group-box}`}
    >
      <Rect
        ref={rectRef}
        x={imageOffset.x + coordinate.x * scaleX}
        y={imageOffset.y + coordinate.y * scaleY}
        width={coordinate.component.width * scaleX}
        height={coordinate.component.height * scaleY}
        fill="transparent"
        data-testid={`flyer-coordinate-box-rect-box}`}
        stroke={
          isAIExtraction
            ? VIEWER_COLORS.AI_EXTRACT_PURPLE
            : isSelected
              ? VIEWER_COLORS.PRIMARY_TEAL
              : VIEWER_COLORS.PRIMARY_ORANGE
        }
        strokeWidth={
          isSelected
            ? COORDINATE_BOX_CONSTANTS.BOX.STROKE_WIDTH_SELECTED
            : COORDINATE_BOX_CONSTANTS.BOX.STROKE_WIDTH_DEFAULT
        }
        draggable={isSelected}
        onDblClick={onSelect}
        onDblTap={onSelect}
        onMouseEnter={(e) => {
          setIsHovered(true);
          if (isSelected && groupRef.current) {
            groupRef.current.moveToTop();
            groupRef.current.getLayer()?.batchDraw();
          }
          const container = e.target.getStage()?.container();
          if (container)
            container.style.cursor = isSelected ? "move" : "pointer";
        }}
        onMouseLeave={(e) => {
          setIsHovered(false);
          const container = e.target.getStage()?.container();
          if (container) container.style.cursor = "default";
        }}
        onDragStart={() => {
          onDragStart();
        }}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onTransformEnd={handleTransformEnd}
      />

      {isSelected && (
        <Transformer
          ref={transformerRef}
          ignoreStroke={false}
          keepRatio={false}
        />
      )}

      {isHovered && isSelected && !hideInfoOverlay && (
        <>
          <Line
            points={[
              connectorStartX,
              connectorStartY,
              connectorEndX,
              connectorEndY,
            ]}
            stroke={VIEWER_COLORS.PRIMARY_TEAL}
            strokeWidth={COORDINATE_BOX_CONSTANTS.CONNECTOR.STROKE_WIDTH}
            listening={false}
          />

          <Circle
            x={connectorEndX}
            y={connectorEndY}
            radius={COORDINATE_BOX_CONSTANTS.CONNECTOR.DOT_RADIUS}
            fill={VIEWER_COLORS.PRIMARY_TEAL}
            listening={false}
          />

          <Rect
            x={cardX + COORDINATE_BOX_CONSTANTS.CARD.SHADOW_OFFSET_PRIMARY}
            y={cardY + COORDINATE_BOX_CONSTANTS.CARD.SHADOW_OFFSET_PRIMARY}
            width={cardWidth}
            height={cardHeight}
            fill={VIEWER_COLORS.SHADOW_LIGHT_1}
            cornerRadius={COORDINATE_BOX_CONSTANTS.CARD.SHADOW_CORNER_RADIUS}
            listening={false}
          />
          <Rect
            x={cardX + COORDINATE_BOX_CONSTANTS.CARD.SHADOW_OFFSET_SECONDARY}
            y={cardY + COORDINATE_BOX_CONSTANTS.CARD.SHADOW_OFFSET_SECONDARY}
            width={cardWidth}
            height={cardHeight}
            fill={VIEWER_COLORS.SHADOW_LIGHT_2}
            cornerRadius={COORDINATE_BOX_CONSTANTS.CARD.SHADOW_CORNER_RADIUS}
            listening={false}
          />

          <Rect
            x={cardX}
            y={cardY}
            width={cardWidth}
            height={cardHeight}
            fill={VIEWER_COLORS.WHITE}
            cornerRadius={COORDINATE_BOX_CONSTANTS.CARD.SHADOW_CORNER_RADIUS}
            listening={false}
          />

          <Rect
            x={cardX}
            y={cardY}
            width={cardWidth}
            height={cardHeight}
            cornerRadius={COORDINATE_BOX_CONSTANTS.CARD.BORDER_CORNER_RADIUS}
            stroke={
              isSelected
                ? VIEWER_COLORS.PRIMARY_TEAL
                : VIEWER_COLORS.LIGHT_GRAY_BORDER
            }
            strokeWidth={
              isSelected
                ? COORDINATE_BOX_CONSTANTS.CARD.BORDER_WIDTH_SELECTED
                : COORDINATE_BOX_CONSTANTS.CARD.BORDER_WIDTH_DEFAULT
            }
            listening={false}
          />

          <Text
            x={cardX + COORDINATE_BOX_CONSTANTS.CARD.TITLE_PADDING_X}
            y={cardY + COORDINATE_BOX_CONSTANTS.CARD.TITLE_PADDING_Y}
            text={displayName}
            fontSize={COORDINATE_BOX_CONSTANTS.TEXT.TITLE_FONT_SIZE}
            fontStyle={COORDINATE_BOX_CONSTANTS.TEXT.TITLE_FONT_STYLE}
            fill={VIEWER_COLORS.TEXT_DARK}
            listening={false}
            width={
              cardWidth - COORDINATE_BOX_CONSTANTS.CARD.CONTENT_WIDTH_SUBTRACT
            }
            wrap="word"
            lineHeight={COORDINATE_BOX_CONSTANTS.TEXT.TITLE_LINE_HEIGHT}
          />

          {detailsText && (
            <>
              <Rect
                x={cardX + COORDINATE_BOX_CONSTANTS.CARD.DETAILS_PILL_PADDING_X}
                y={
                  cardY +
                  COORDINATE_BOX_CONSTANTS.CARD.TITLE_PADDING_Y +
                  titleHeight +
                  COORDINATE_BOX_CONSTANTS.CARD.DETAILS_PILL_TOP_OFFSET
                }
                width={
                  cardWidth -
                  COORDINATE_BOX_CONSTANTS.CARD.DETAILS_PILL_WIDTH_SUBTRACT
                }
                height={
                  detailsHeight +
                  COORDINATE_BOX_CONSTANTS.CARD.DETAILS_PILL_HEIGHT_EXTRA
                }
                fill={VIEWER_COLORS.INFO_PILL_BG}
                cornerRadius={
                  COORDINATE_BOX_CONSTANTS.CARD.DETAILS_PILL_CORNER_RADIUS
                }
                listening={false}
              />

              <Text
                x={cardX + COORDINATE_BOX_CONSTANTS.CARD.DETAILS_TEXT_PADDING_X}
                y={
                  cardY +
                  COORDINATE_BOX_CONSTANTS.CARD.TITLE_PADDING_Y +
                  titleHeight +
                  COORDINATE_BOX_CONSTANTS.CARD.DETAILS_TEXT_PADDING_Y
                }
                text={detailsText}
                fontSize={COORDINATE_BOX_CONSTANTS.TEXT.DETAILS_FONT_SIZE}
                fill={VIEWER_COLORS.INFO_PILL_TEXT}
                fontStyle={COORDINATE_BOX_CONSTANTS.TEXT.DETAILS_FONT_STYLE}
                listening={false}
                width={
                  cardWidth -
                  COORDINATE_BOX_CONSTANTS.CARD.DETAILS_TEXT_WIDTH_SUBTRACT
                }
                wrap="word"
                lineHeight={COORDINATE_BOX_CONSTANTS.TEXT.DETAILS_LINE_HEIGHT}
              />
            </>
          )}
        </>
      )}

      {isSelected && onEdit && !isAIExtraction && !hideInfoOverlay && (
        <Group
          x={boxEditIconX}
          y={boxEditIconY}
          onClick={handleEditClick}
          onTap={handleEditClick}
          data-testid={`flyer-coordinate-box-edit-btn-box}`}
          onMouseEnter={(e) => {
            setIsEditHovered(true);
            const container = e.target.getStage()?.container();
            if (container) {
              container.style.cursor = "pointer";
            }
          }}
          onMouseLeave={(e) => {
            setIsEditHovered(false);
            const container = e.target.getStage()?.container();
            if (container) {
              container.style.cursor = isSelected ? "move" : "default";
            }
          }}
        >
          <Circle
            radius={
              editIconSize / 2 +
              COORDINATE_BOX_CONSTANTS.EDIT_ICON.HOVER_RING_PADDING
            }
            x={editIconSize / 2}
            y={editIconSize / 2}
            fill={
              isEditHovered
                ? VIEWER_COLORS.ACCENT_TEAL_TRANSPARENT
                : "transparent"
            }
          />
          <Circle
            radius={editIconSize / 2}
            x={editIconSize / 2}
            y={editIconSize / 2}
            fill={
              isEditHovered ? VIEWER_COLORS.PRIMARY_TEAL : VIEWER_COLORS.WHITE
            }
            stroke={
              isEditHovered
                ? VIEWER_COLORS.PRIMARY_TEAL
                : VIEWER_COLORS.GRAY_300
            }
            strokeWidth={
              isEditHovered
                ? COORDINATE_BOX_CONSTANTS.EDIT_ICON.STROKE_WIDTH_HOVER
                : COORDINATE_BOX_CONSTANTS.EDIT_ICON.STROKE_WIDTH_DEFAULT
            }
            shadowColor={VIEWER_COLORS.SHADOW_SOFT}
            shadowBlur={COORDINATE_BOX_CONSTANTS.EDIT_ICON.SHADOW_BLUR}
            shadowOffsetY={COORDINATE_BOX_CONSTANTS.EDIT_ICON.SHADOW_OFFSET_Y}
          />
          <Group x={editIconSize / 2} y={editIconSize / 2}>
            <Line
              points={COORDINATE_BOX_CONSTANTS.PENCIL_ICON.BODY_POINTS}
              stroke={
                isEditHovered ? VIEWER_COLORS.WHITE : VIEWER_COLORS.PRIMARY_TEAL
              }
              strokeWidth={
                COORDINATE_BOX_CONSTANTS.PENCIL_ICON.STROKE_WIDTH_MAIN
              }
              lineCap="round"
              lineJoin="round"
            />
            <Line
              points={COORDINATE_BOX_CONSTANTS.PENCIL_ICON.TIP_POINTS}
              stroke={
                isEditHovered
                  ? VIEWER_COLORS.WHITE
                  : VIEWER_COLORS.PRIMARY_ORANGE
              }
              strokeWidth={
                COORDINATE_BOX_CONSTANTS.PENCIL_ICON.STROKE_WIDTH_MAIN
              }
              lineCap="round"
              lineJoin="round"
            />
            <Line
              points={COORDINATE_BOX_CONSTANTS.PENCIL_ICON.DETAIL_POINTS}
              stroke={
                isEditHovered ? VIEWER_COLORS.WHITE : VIEWER_COLORS.PRIMARY_TEAL
              }
              strokeWidth={
                COORDINATE_BOX_CONSTANTS.PENCIL_ICON.STROKE_WIDTH_DETAIL
              }
              lineCap="round"
            />
          </Group>
        </Group>
      )}
    </Group>
  );
};
